import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BlogCTA from '../components/BlogCTA.jsx';
import ScrollSignupPopup from '../components/ScrollSignupPopup.jsx';
import { useInitialData } from '../ssr/InitialDataProvider.jsx';
import { setPageSEO } from '../utils/seo.js';
import { getBlogBySlug, getPublishedBlogs, slugify } from '../utils/blogApi.js';
import './Blog.css';

function formatDate(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
}

function detailIsVisible(value) {
  return value !== undefined && value !== null && value !== '';
}

function getRelatedBlogs(currentBlog, blogs) {
  const currentTags = new Set(currentBlog.tags || []);
  const currentContentType = currentBlog.contentTypeSlug || slugify(currentBlog.contentType || currentBlog.category);

  return blogs
    .filter((candidate) => candidate.slug && candidate.slug !== currentBlog.slug)
    .map((candidate) => {
      const candidateContentType = candidate.contentTypeSlug || slugify(candidate.contentType || candidate.category);
      const sharedTags = (candidate.tags || []).filter((tag) => currentTags.has(tag)).length;
      const relevance = sharedTags * 2 + (candidateContentType === currentContentType ? 1 : 0);

      return { candidate, relevance };
    })
    .sort((first, second) => (
      second.relevance - first.relevance
      || new Date(second.candidate.publishedAt || 0) - new Date(first.candidate.publishedAt || 0)
    ))
    .slice(0, 3)
    .map(({ candidate }) => candidate);
}

function BlogPost() {
  const { contentType, slug } = useParams();
  const navigate = useNavigate();
  const initialBlog = useInitialData(`blog:${slug}`);
  const initialPublished = useInitialData('publishedBlogs');

  const [blog, setBlog] = useState(initialBlog || null);
  const [relatedBlogs, setRelatedBlogs] = useState(initialPublished ? getRelatedBlogs(initialBlog || {}, initialPublished) : []);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadBlogPost() {
      setIsLoading(true);
      setError('');
      try {
        if (initialBlog) {
          // we already have server-provided data
          if (isMounted) {
            setBlog(initialBlog);
            setRelatedBlogs(getRelatedBlogs(initialBlog, initialPublished || []));
          }
        } else {
          const [blogPost, publishedBlogs] = await Promise.all([
            getBlogBySlug(slug),
            getPublishedBlogs().catch(() => [])
          ]);

          if (!isMounted) {
            return;
          }

          setBlog(blogPost);
          setRelatedBlogs(getRelatedBlogs(blogPost, publishedBlogs));
        }
      } catch (requestError) {
        if (isMounted) {
          setError('We could not load this blog post right now. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadBlogPost();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!blog) {
      return undefined;
    }

    return setPageSEO({
      title: `${blog.metaTitle || blog.title} - Clientalio`,
      description: blog.metaDescription,
      canonical: `https://clientalio.com/${blog.contentTypeSlug || slugify(blog.contentType || blog.category)}/${blog.slug || slug}`,
      ogTitle: `${blog.metaTitle || blog.title} - Clientalio`,
      ogDescription: blog.metaDescription,
      twitterTitle: `${blog.metaTitle || blog.title} - Clientalio`,
      twitterDescription: blog.metaDescription
    });
  }, [blog, slug]);

  const contentTypePath = blog?.contentTypeSlug || slugify(blog?.contentType || blog?.category);

  useEffect(() => {
    if (blog && contentType !== contentTypePath) {
      navigate(`/${contentTypePath}/${blog.slug || slug}`, { replace: true });
    }
  }, [blog, contentType, contentTypePath, navigate, slug]);

  return (
    <main className="blog-page blog-post-page">
      <section className="blog-content">
        <div className="container blog-article-wrap">
          <Link to={`/${contentTypePath}`} className="blog-back-link">Back to {blog?.contentType || blog?.category || 'blog'}</Link>

          {isLoading && <p className="blog-status">Loading blog post...</p>}
          {!isLoading && error && <p className="blog-status blog-status-error">{error}</p>}

          {!isLoading && !error && blog && (
            <>
              <article className="blog-article">
                {blog.bannerImage && <img src={blog.bannerImage} alt="" className="blog-article-banner" />}
                <header className="blog-article-header">
                  <div className="blog-meta">
                    {(blog.contentType || blog.category) && <span>{blog.contentType || blog.category}</span>}
                    {formatDate(blog.publishedAt) && <time dateTime={blog.publishedAt}>{formatDate(blog.publishedAt)}</time>}
                    {blog.readingTime && <span>{blog.readingTime} min read</span>}
                  </div>
                  <h1>{blog.title}</h1>
                  {blog.excerpt && <p>{blog.excerpt}</p>}
                  {(blog.author || blog.category) && (
                    <div className="blog-article-byline">
                      {blog.author && <span>By {blog.author}</span>}
                      {blog.category && <span>{blog.category}</span>}
                    </div>
                  )}
                  {blog.imageUrl && <img src={blog.imageUrl} alt="" className="blog-article-image" />}
                </header>

                {blog.content ? (
                  <div className="blog-article-content" dangerouslySetInnerHTML={{ __html: blog.content }} />
                ) : (
                  <p className="blog-status">This article does not have content yet.</p>
                )}

                <section className="blog-article-details" aria-label="Article details">
                  {[
                    ['Content type', blog.contentType],
                    ['Category', blog.category],
                    ['Author', blog.author],
                    ['Reading time', blog.readingTime ? `${blog.readingTime} minutes` : ''],
                    ['Published', formatDate(blog.publishedAt)],
                    ['Updated', formatDate(blog.updatedAt)],
                    ['Views', detailIsVisible(blog.viewCount) ? blog.viewCount : ''],
                    ['Featured', blog.isFeatured ? 'Yes' : 'No'],
                    ['Comments', blog.allowComments ? 'Allowed' : 'Disabled']
                  ].filter(([, value]) => detailIsVisible(value)).map(([label, value]) => (
                    <div className="blog-article-detail" key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </section>

                {blog.tags.length > 0 && (
                  <footer className="blog-article-tags">
                    {blog.tags.map((tag) => <span key={tag}>{tag}</span>)}
                  </footer>
                )}
              </article>

              {relatedBlogs.length > 0 && (
                <section className="blog-related" aria-labelledby="related-articles-title">
                  <h2 id="related-articles-title">Keep reading</h2>
                  <div className="blog-grid">
                    {relatedBlogs.map((relatedBlog) => (
                      <article className="blog-card" key={relatedBlog.id || relatedBlog.slug}>
                        {relatedBlog.imageUrl && <img src={relatedBlog.imageUrl} alt="" className="blog-card-image" loading="lazy" />}
                        <div className="blog-card-body">
                          <div className="blog-meta">
                            {(relatedBlog.contentType || relatedBlog.category) && <span>{relatedBlog.contentType || relatedBlog.category}</span>}
                            {formatDate(relatedBlog.publishedAt) && <time dateTime={relatedBlog.publishedAt}>{formatDate(relatedBlog.publishedAt)}</time>}
                          </div>
                          <h3>{relatedBlog.title}</h3>
                          {relatedBlog.excerpt && <p>{relatedBlog.excerpt}</p>}
                          <Link
                            to={`/${relatedBlog.contentTypeSlug || slugify(relatedBlog.contentType || relatedBlog.category)}/${relatedBlog.slug}`}
                            className="blog-read-link"
                          >
                            Read article
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              )}
              <ScrollSignupPopup slug={blog.slug || slug} signupUrl="https://clientalio.com/signup" />
            </>
          )}
        </div>
      </section>

      <BlogCTA location="blog_post_cta" />
    </main>
  );
}

export default BlogPost;